import { useEffect, useState } from 'react'
import RoutesMap from 'route_control/RouteControl'
import { Form } from 'react-bootstrap'
import { useFormik } from 'formik'
import { Text, Email, File, Number, Password, SelectBox, MultiSelectBox, SelectStatusBox, DatePicker } from 'components/input_fields'
import { UpdatePremiseUserSchema } from './ValidationSchema'
import { SaveButton } from 'components/buttons'
import Loader from 'components/loader/Loader'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { userRoleList } from '../../../../redux/slices/settings'
import { premiseList } from '../../../../redux/slices/premises'
import { updatePremiseUser, premiseUserDetails } from '../../../../redux/slices/users'
import { addUpdateHandler, SessionManager } from 'utils'
import { useTranslation } from 'react-i18next'

export default function UpdatePremiseUserForm() {

    const [loading, setLoading] = useState(true)
    const [breakLoading, setBreakLoading] = useState(true)
    const [spinner, setSpinner] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const { t } = useTranslation()
    const { state } = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const user = useSelector(state => state?.premiseUser?.details)
    const role = useSelector(state => state?.userRole?.list)
    const premise = useSelector(state => state?.premise?.list)

    const role_option = role?.map(val => (
        { "value": val.id, "label": val.role_name }
    ))

    const premise_option = premise?.map(val => (
        { "value": val.id, "label": val.name }
    ))

    const gender_option = [
        { "value": 'Male', "label": 'Male' },
        { "value": 'Female', "label": 'Female' },
        { "value": 'Other', "label": 'Other' },
    ]

    const user_data = JSON.parse(SessionManager.getCookie("user"))
    const showPasswordFields = JSON.parse(SessionManager.getCookie("role"))?.role_id === 3 || JSON.parse(SessionManager.getCookie("role"))?.role_id === 4

    const selectedRoleIds = Array.isArray(user?.userRoles) ? user?.userRoles?.map(r => r?.role_id) : []
    const initialIsStaff = selectedRoleIds?.some(roleId =>
        role?.find(r => r.id === roleId)?.is_staff
    ) || false

    const initialValues = {
        id: user?.user_id,
        premise_id: user?.premise_id,
        role_id: selectedRoleIds,
        is_staff: initialIsStaff,
        joining_date: user?.joining_date && moment(user?.joining_date, 'YYYY-MM-DD').format('DD/MM/YYYY') || '',
        salary: user?.salary_amount ?? '',
        fname: user?.first_name,
        lname: user?.last_name,
        gender: user?.gender,
        username: user?.loginInfo?.user_name,
        password: '',
        mobile: user?.user_mobile,
        email: user?.user_email,
        dob: user?.dob && moment(user?.dob, 'YYYY-MM-DD').format('DD/MM/YYYY') || '',
        profile: '',
        status: user?.status
    }

    const { values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, setFieldTouched } = useFormik({
        initialValues: initialValues,
        validationSchema: UpdatePremiseUserSchema,
        enableReinitialize: true,

        onSubmit: (values, action) => {
            setSpinner(true)
            dispatch(updatePremiseUser({ 'values': values })).then((res) => addUpdateHandler({
                res: res.payload,
                action,
                navigate,
                success_toast: `${t('premiseUser.premise_user')} ${t('common.success_update')}`,
                wrong_toast: `${t('common.wrong')}`,
                navigate_route: RoutesMap.PREMISE_USER_LIST.path,
                spinner: setSpinner,
            }))
        },
    })

    const handleRoleChange = (selectedRoles) => {
        setFieldValue('role_id', selectedRoles)
        const isStaff = selectedRoles?.some(roleId =>
            role?.find(r => r.id === roleId)?.is_staff
        ) || false
        setFieldValue('is_staff', isStaff)
        if (!isStaff) {
            setFieldValue('joining_date', '')
            setFieldValue('salary', '')
        }
    }

    const user_res = async (res) => {
        try {
            if (res?.status === 200) {
                const pageable = { 'role_filter': 'below' }
                await Promise.all([
                    dispatch(userRoleList({ pageable, navigate })),
                    dispatch(premiseList({ navigate }))
                ]);
                setLoading(false)
                setBreakLoading(false)
            } else {
                setBreakLoading(false)
            }
        } catch (error) {
            setBreakLoading(false)
        }
    }

    useEffect(() => {
        setLoading(true)
        setBreakLoading(true)
        dispatch(premiseUserDetails({ 'id': state, navigate })).then((res) => user_res(res.payload))
    }, [])

    return (
        <>
            <div className="container-fluid">
                <div className='layout'>
                    {loading && breakLoading ?
                        <div className="row">
                            <div className="col-md-12">
                                <Loader />
                            </div>
                        </div>
                        :

                        loading && !breakLoading ?

                            <div className='text-center'>
                                <h5>Something went wrong can't able to load update form</h5>
                            </div>

                            :

                            !loading && !breakLoading ?

                                <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                                    <div className="row">

                                        <div className="col-md-3">
                                            <SelectBox
                                                label_name={t('premiseUser.premise')}
                                                mandatory={true}
                                                placeholder=''
                                                disabled={user_data?.premise_id}
                                                option={premise_option}
                                                name='premise_id'
                                                value={values.premise_id}
                                                onChange={(e) =>
                                                    setFieldValue('premise_id', e)
                                                }
                                                onBlur={() => setFieldTouched("premise_id", true)}
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                error={errors.premise_id && touched.premise_id ? (<span className='field_error'>{errors.premise_id}</span>) : null}
                                            />
                                        </div>

                                        <div className="col-md-3">
                                            <MultiSelectBox
                                                label_name={t('premiseUser.role')}
                                                mandatory={true}
                                                placeholder=''
                                                disabled={false}
                                                option={role_option}
                                                name='role_id'
                                                value={values.role_id}
                                                onChange={handleRoleChange}
                                                onBlur={() => setFieldTouched("role_id", true)}
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                error={errors.role_id && touched.role_id ? (<span className='field_error'>{errors.role_id}</span>) : null}
                                            />
                                        </div>

                                        {values?.is_staff && (
                                            <>
                                                <div className="col-md-3">
                                                    <DatePicker
                                                        label_name={t('premiseUser.joining_date')}
                                                        mandatory={true}
                                                        placeholder=''
                                                        name='joining_date'
                                                        value={values.joining_date}
                                                        onChange={(date, dateString) => setFieldValue('joining_date', dateString)}
                                                        onBlur={() => setFieldTouched('joining_date', true)}
                                                        error={errors.joining_date && touched.joining_date ? (<span className='field_error'>{errors.joining_date}</span>) : null}
                                                    />
                                                </div>

                                                <div className="col-md-3">
                                                    <Number
                                                        label_name={t('premiseUser.salary')}
                                                        mandatory={true}
                                                        placeholder=''
                                                        disabled={false}
                                                        name='salary'
                                                        value={values.salary}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={errors.salary && touched.salary ? (<span className='field_error'>{errors.salary}</span>) : null}
                                                    />
                                                </div>
                                            </>
                                        )}

                                        <div className="col-md-3">
                                            <Text
                                                label_name={t('premiseUser.fname')}
                                                mandatory={true}
                                                placeholder=''
                                                disabled={false}
                                                name='fname'
                                                value={values.fname}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.fname && touched.fname ? (<span className='field_error'>{errors.fname}</span>) : null}
                                            />
                                        </div>

                                        <div className="col-md-3">
                                            <Text
                                                label_name={t('premiseUser.lname')}
                                                mandatory={true}
                                                placeholder=''
                                                disabled={false}
                                                name='lname'
                                                value={values.lname}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.lname && touched.lname ? (<span className='field_error'>{errors.lname}</span>) : null}
                                            />
                                        </div>

                                        <div className="col-md-3">
                                            <SelectBox
                                                label_name={t('premiseUser.gender')}
                                                mandatory={true}
                                                placeholder=''
                                                disabled={false}
                                                option={gender_option}
                                                name='gender'
                                                value={values.gender}
                                                onChange={(e) =>
                                                    setFieldValue('gender', e)
                                                }
                                                onBlur={() => setFieldTouched("gender", true)}
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                error={errors.gender && touched.gender ? (<span className='field_error'>{errors.gender}</span>) : null}
                                            />
                                        </div>

                                        <div className="col-md-3">
                                            <Text
                                                label_name={t('premiseUser.username')}
                                                mandatory={true}
                                                placeholder=''
                                                disabled={false}
                                                name='username'
                                                value={values.username}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.username && touched.username ? (<span className='field_error'>{errors.username}</span>) : null}
                                            />
                                        </div>

                                        {showPasswordFields && (
                                            <>
                                                <div className="col-md-3">
                                                    <Password
                                                        label_name={t('premiseUser.password')}
                                                        mandatory={false}
                                                        placeholder='*******'
                                                        disabled={false}
                                                        name='password'
                                                        value={values.password}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        showPassword={showPassword}
                                                        setShowPassword={setShowPassword}
                                                        error={errors.password && touched.password ? (<span className='field_error'>{errors.password}</span>) : null}
                                                    />
                                                </div>
                                            </>
                                        )}

                                        <div className="col-md-3">
                                            <Number
                                                label_name={t('premiseUser.mobile')}
                                                mandatory={true}
                                                placeholder=''
                                                disabled={false}
                                                name='mobile'
                                                value={values.mobile}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.mobile && touched.mobile ? (<span className='field_error'>{errors.mobile}</span>) : null}
                                            />
                                        </div>

                                        <div className="col-md-3">
                                            <Email
                                                label_name={t('premiseUser.email')}
                                                mandatory={true}
                                                placeholder=''
                                                disabled={false}
                                                name='email'
                                                value={values.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.email && touched.email ? (<span className='field_error'>{errors.email}</span>) : null}
                                            />
                                        </div>

                                        <div className="col-md-3">
                                            <DatePicker
                                                label_name={t('premiseUser.dob')}
                                                mandatory={false}
                                                placeholder=''
                                                name='dob'
                                                value={values.dob}
                                                onChange={(date, dateString) => setFieldValue('dob', dateString)}
                                                onBlur={() => setFieldTouched('dob', true)}
                                                error={errors.dob && touched.dob ? (<span className='field_error'>{errors.dob}</span>) : null}
                                            />
                                        </div>

                                        <div className="col-md-3">
                                            <File
                                                label_name={t('premiseUser.profile')}
                                                mandatory={false}
                                                disabled={false}
                                                name='profile'
                                                onChange={(e) =>
                                                    setFieldValue('profile', e.currentTarget.files[0])
                                                }
                                                onBlur={handleBlur}
                                                error={errors.profile && touched.profile ? (<span className='field_error'>{errors.profile}</span>) : null}
                                                accept="image/jpg, image/jpeg, image/png"
                                            />
                                        </div>

                                        <div className="col-md-3">
                                            <SelectStatusBox
                                                label_name={t('premiseUser.status')}
                                                mandatory={true}
                                                placeholder=''
                                                disabled={false}
                                                name='status'
                                                value={values.status}
                                                onChange={(e) =>
                                                    setFieldValue('status', e)
                                                }
                                                onBlur={() => setFieldTouched("status", true)}
                                                error={errors.status && touched.status ? (<span className='field_error'>{errors.status}</span>) : null}
                                            />
                                        </div>

                                        <div className="col-md-12">
                                            <div className="d-flex justify-content-end">
                                                <div className="col-lg-2">
                                                    <SaveButton
                                                        button_name={t('common.submit')}
                                                        spinner={spinner}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </Form>
                                : ''
                    }
                </div>
            </div>
        </>
    )
}